import { isInvestigationConclusion } from './watchInvestigation';

import type {
  WatchCompany,
  WatchFinding,
  WatchRegistryFilters,
  WatchRegistryRow,
  WatchRun,
  WatchSchedule,
  WatchTowerMockState,
} from './types';

const ELEVATED_SEVERITIES = new Set(['high', 'critical']);
const IDENTIFIER_SCHEME_ORDER = ['lei', 'duns'];

export function firstIdentifierLabel(company: WatchCompany): string {
  const byScheme = new Map(
    company.identifiers.map((item) => [item.scheme.toLowerCase(), item.value]),
  );
  const ordered = IDENTIFIER_SCHEME_ORDER
    .map((scheme) => byScheme.get(scheme))
    .find(Boolean);
  if (ordered) {
    return ordered;
  }
  return company.identifiers[0]?.value || '—';
}

export function agentTypesForCompany(
  companyId: number,
  schedules: WatchSchedule[],
): string[] {
  const types = schedules
    .filter((schedule) => schedule.watchCompanyId === companyId && !schedule.paused)
    .map((schedule) => schedule.type);
  return Array.from(new Set(types));
}

export function lastRunAtForCompany(companyId: number, runs: WatchRun[]): string | null {
  const stamps = runs
    .filter((run) => run.watchCompanyId === companyId)
    .map((run) => run.scheduledAt)
    .sort();
  return stamps.length ? stamps[stamps.length - 1] : null;
}

export function openFindingCount(
  companyId: number,
  findings: WatchFinding[],
): number {
  return findings.filter(
    (finding) => finding.watchCompanyId === companyId
      && ELEVATED_SEVERITIES.has(finding.severity)
      && !isInvestigationConclusion(finding),
  ).length;
}

export function toRegistryRow(
  company: WatchCompany,
  state: WatchTowerMockState,
): WatchRegistryRow {
  return {
    company,
    identifierLabel: firstIdentifierLabel(company),
    productCount: company.products.length,
    buildingCount: company.buildings.length,
    agentTypes: agentTypesForCompany(company.id, state.schedules),
    lastRunAt: lastRunAtForCompany(company.id, state.runs),
    openFindingCount: openFindingCount(company.id, state.findings),
  };
}

export function filterWatchCompanies(
  state: WatchTowerMockState,
  filters: WatchRegistryFilters,
): WatchRegistryRow[] {
  const search = filters.search.trim().toLowerCase();
  return state.watchCompanies
    .filter((company) => {
      if (filters.status === 'visible') {
        return company.status !== 'archived';
      }
      if (filters.status !== 'all') {
        return company.status === filters.status;
      }
      return true;
    })
    .filter((company) => {
      if (!search) {
        return true;
      }
      const haystack = [
        company.name,
        company.code || '',
        company.uid,
        company.addressCountry,
        company.addressCity || '',
        ...company.aliases,
      ].join(' ').toLowerCase();
      return haystack.includes(search);
    })
    .filter((company) => !filters.country || company.addressCountry === filters.country)
    .filter((company) => !filters.industry || company.naceSection === filters.industry)
    .filter((company) => {
      if (!filters.agentType) {
        return true;
      }
      return agentTypesForCompany(company.id, state.schedules).includes(filters.agentType);
    })
    .filter((company) => {
      if (filters.coverage === 'with_agents') {
        return agentTypesForCompany(company.id, state.schedules).length > 0;
      }
      if (filters.coverage === 'without_agents') {
        return agentTypesForCompany(company.id, state.schedules).length === 0;
      }
      if (filters.coverage === 'awaiting_review') {
        return company.aiStatus === 'awaiting_human_review';
      }
      return true;
    })
    .map((company) => toRegistryRow(company, state));
}

export function uniqueCountries(state: WatchTowerMockState): string[] {
  return Array.from(new Set(
    state.watchCompanies.map((company) => company.addressCountry).filter(Boolean),
  )).sort();
}

export function uniqueIndustries(state: WatchTowerMockState): string[] {
  return Array.from(new Set(
    state.watchCompanies
      .map((company) => company.naceSection)
      .filter((value): value is string => Boolean(value)),
  )).sort();
}

export function uniqueAgentTypes(state: WatchTowerMockState): string[] {
  return Array.from(new Set(state.schedules.map((schedule) => schedule.type))).sort();
}

export function nextWatchCompanyId(state: WatchTowerMockState): number {
  return state.watchCompanies.reduce((max, company) => Math.max(max, company.id), 0) + 1;
}

export function createDraftWatchCompany(id: number): WatchCompany {
  return {
    id,
    name: 'New watch company',
    code: null,
    uid: `WT-NEW-${id}`,
    ownership: null,
    dateOpened: null,
    dateClosed: null,
    naceSection: null,
    naceCode: null,
    euTaxonomyCode: null,
    addressCountry: '',
    addressState: null,
    addressCity: null,
    addressPostCode: null,
    addressStreet: null,
    addressBuilding: null,
    addressCoordinates: null,
    website: null,
    status: 'draft',
    notes: null,
    identifiers: [],
    aliases: [],
    products: [],
    buildings: [],
    orgNodes: [],
    associations: [],
    financials: [],
    aiStatus: 'idle',
    aiErrorMessage: null,
    aiSuggestion: null,
  };
}

export function formatIsoDate(value: string | null): string {
  if (!value) {
    return '—';
  }
  return value.slice(0, 10);
}

export const AGENT_TYPE_LABELS: Record<string, string> = {
  'reputation-monitoring': 'Public Sentiment',
  'regulation-monitoring': 'Product Regulation',
  'finance-monitoring': 'Financial Stability',
  'climate-monitoring': 'Climate',
  'ownership-monitoring': 'Ownership',
};

export function formatAgentType(type: string): string {
  return AGENT_TYPE_LABELS[type] || type;
}

export interface WatchCompanyCoverageSummary {
  visibleCount: number,
  withAgentsCount: number,
  withoutAgentsCount: number,
  awaitingReviewCount: number,
  openFindingsCount: number,
}

export function watchCompanyCoverageSummary(
  state: WatchTowerMockState,
): WatchCompanyCoverageSummary {
  const visible = state.watchCompanies.filter((company) => company.status !== 'archived');
  const withAgentsCount = visible.filter(
    (company) => agentTypesForCompany(company.id, state.schedules).length > 0,
  ).length;
  const awaitingReviewCount = visible.filter(
    (company) => company.aiStatus === 'awaiting_human_review',
  ).length;
  const openFindingsCount = visible.reduce(
    (sum, company) => sum + openFindingCount(company.id, state.findings),
    0,
  );
  return {
    visibleCount: visible.length,
    withAgentsCount,
    withoutAgentsCount: visible.length - withAgentsCount,
    awaitingReviewCount,
    openFindingsCount,
  };
}
